import React, { useState, useEffect, useRef } from "react";

import { notificationAction } from '../../redux/actions';
import { useSelector, useDispatch } from 'react-redux';
import { notificationSelect } from '../../redux/selectors/index';
import { store } from "../../redux/store";
import { Images } from "./index";
import {
    RiCloseFill,
    RiCheckDoubleFill,
    RiCheckboxCircleFill,
    RiErrorWarningFill,
    RiInformationFill,
    RiCloseLine
} from "react-icons/ri";

let hideIn = () => new Date(Date.now() + 6000);

export const show = (msg, code) => {
    if (typeof msg === 'string' && 
        (msg.trim().toLowerCase() === 'network request failed. please check internet connection.' ||
         msg.trim().toLowerCase() === 'network request failed. please check internet connection')) {
        return;
    }
    store.dispatch(notificationAction.onChangeNotification({
        show: true,
        msg: msg,
        code: code,
        hideAfter: hideIn()
    }));
}

export const hide = () => {
    store.dispatch(notificationAction.onChangeNotification({ show: false }));
}

let timer = 0;

function Notify() {
    const notificationData = useSelector(notificationSelect);
    const [isVisible, setIsVisible] = useState(false);
    const [progress, setProgress] = useState(100);
    const progressInterval = useRef(null);
    const notificationRef = useRef(null);

    const hideNotification = () => {
        setIsVisible(false);
        setTimeout(() => {
            hide();
            setProgress(100);
        }, 300);
    }

    const getNotificationConfig = (code) => {
        switch (code) {
            case 1: // Success
                return {
                    title: "Action Completed",
                    metaLabel: "// TRANSACTION_SUCCESS // SECURE_NODE",
                    color: "#10b981",
                    colorGlow: "rgba(16, 185, 129, 0.4)",
                    colorBorder: "rgba(16, 185, 129, 0.35)",
                    iconSvg: (
                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                            <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    )
                };
            case 0: // Error
                return {
                    title: "Notice / Warning",
                    metaLabel: "// EXECUTION_FAILED // EXCEPTION_ALERT",
                    color: "#f43f5e",
                    colorGlow: "rgba(244, 63, 94, 0.4)",
                    colorBorder: "rgba(244, 63, 94, 0.35)",
                    iconSvg: (
                        <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                            <path d="M1 1L7 7M7 1L1 7" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                        </svg>
                    )
                };
            default: // Info
                return {
                    title: "System Update",
                    metaLabel: "// STATUS_REPORT // CLOUD_SYNC",
                    color: "#3b82f6",
                    colorGlow: "rgba(59, 130, 246, 0.4)",
                    colorBorder: "rgba(59, 130, 246, 0.35)",
                    iconSvg: (
                        <svg width="2" height="6" viewBox="0 0 2 6" fill="none">
                            <path d="M1 1V5" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                            <circle cx="1" cy="0.5" r="0.5" fill="white"/>
                        </svg>
                    )
                };
        }
    };

    useEffect(() => {
        clearTimeout(timer);
        clearInterval(progressInterval.current);

        if (notificationData?.show) {
            setIsVisible(true);
            const duration = notificationData?.hideAfter - Date.now();

            // Start progress bar countdown
            progressInterval.current = setInterval(() => {
                const remaining = notificationData?.hideAfter - Date.now();
                const percentage = Math.max(0, (remaining / 6000) * 100);
                setProgress(percentage);

                if (percentage <= 0) {
                    clearInterval(progressInterval.current);
                }
            }, 50);

            // Auto hide timer
            timer = setTimeout(() => {
                hideNotification();
            }, duration);
        } else {
            setIsVisible(false);
            setProgress(100);
        }

        return () => {
            clearTimeout(timer);
            clearInterval(progressInterval.current);
        };
    }, [notificationData?.show, notificationData?.hideAfter]);

    if (notificationData?.show) {
        const config = getNotificationConfig(notificationData?.code);
        const radius = 16.5;
        const circumference = 2 * Math.PI * radius; // ~103.67
        const strokeDashoffset = circumference - (progress / 100) * circumference;

        return (
            <div
                className={`notify-overlay ${isVisible ? 'show' : ''}`}
                onClick={hideNotification}
                role="dialog"
                aria-modal="true"
                aria-labelledby="notification-title"
                aria-describedby="notification-message"
            >
                {/* Luminous Hologram HUD Capsule */}
                <div
                    ref={notificationRef}
                    className="hologram-hud-capsule"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        '--notification-color': config.color,
                        '--notification-color-glow': config.colorGlow,
                        '--notification-color-border': config.colorBorder,
                    }}
                >
                    {/* Hairline Edge Glint Flare */}
                    <div className="corner-light-flare" />

                    {/* Ring progress and breathing central orb */}
                    <div className="status-ring-container">
                        <svg width="36" height="36" viewBox="0 0 36 36" className="progress-ring-svg">
                            <circle
                                cx="18"
                                cy="18"
                                r={radius}
                                stroke="rgba(15, 23, 42, 0.04)"
                                strokeWidth="1.5"
                                fill="transparent"
                            />
                            <circle
                                cx="18"
                                cy="18"
                                r={radius}
                                stroke="var(--notification-color)"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeDasharray={circumference}
                                strokeDashoffset={strokeDashoffset}
                                fill="transparent"
                                className="progress-ring-path"
                            />
                        </svg>
                        <div className="fluid-emblem-orb">
                            <div className="orb-inner-flare" />
                            <div className="orb-icon-wrap">
                                {config.iconSvg}
                            </div>
                        </div>
                    </div>

                    {/* Content Section */}
                    <div className="capsule-content-section">
                        <div className="hud-meta-label">
                            {config.metaLabel}
                        </div>
                        <h4 id="notification-title" className="capsule-title">
                            {config.title}
                        </h4>
                        <p id="notification-message" className="capsule-message">
                            {notificationData?.msg}
                        </p>
                    </div>

                    {/* Right Close Button */}
                    <button
                        className="capsule-close-btn"
                        onClick={hideNotification}
                        aria-label="Close notification"
                    >
                        <RiCloseLine size={16} />
                    </button>
                </div>

                <style jsx>{`
                    .notify-overlay {
                        position: fixed;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: rgba(15, 23, 42, 0.16);
                        backdrop-filter: blur(12px);
                        -webkit-backdrop-filter: blur(12px);
                        z-index: 99999;
                        display: flex;
                        align-items: flex-start;
                        justify-content: center;
                        padding-top: 32px;
                        opacity: 0;
                        visibility: hidden;
                        transition: opacity 0.35s cubic-bezier(0.16, 1, 0.3, 1), 
                                    visibility 0.35s cubic-bezier(0.16, 1, 0.3, 1);
                    }

                    .notify-overlay.show {
                        opacity: 1;
                        visibility: visible;
                    }

                    /* Hologram HUD Glass Capsule */
                    .hologram-hud-capsule {
                        pointer-events: auto;
                        display: flex;
                        align-items: center;
                        gap: 16px;
                        padding: 14px 20px 14px 16px;
                        background: linear-gradient(rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.91)) padding-box, 
                                    linear-gradient(135deg, var(--notification-color) 0%, rgba(255, 255, 255, 0.4) 45%, var(--notification-color-border) 70%, rgba(15, 23, 42, 0.08) 100%) border-box;
                        border: 1px solid transparent;
                        border-radius: 24px;
                        backdrop-filter: blur(30px) saturate(200%);
                        -webkit-backdrop-filter: blur(30px) saturate(200%);
                        box-shadow: 0 10px 30px -8px var(--notification-color-glow), 
                                    0 20px 40px -12px rgba(15, 23, 42, 0.18), 
                                    0 0 0 1px rgba(15, 23, 42, 0.04), 
                                    inset 0 1px 0 rgba(255, 255, 255, 0.85);
                        max-width: 420px;
                        width: calc(100% - 32px);
                        position: relative;
                        overflow: hidden;
                        transform: translateY(-32px) scale(0.95);
                        opacity: 0;
                        transition: transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275), 
                                    opacity 0.4s ease;
                        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
                    }

                    .notify-overlay.show .hologram-hud-capsule {
                        transform: translateY(0) scale(1);
                        opacity: 1;
                        animation: capsuleFloat 6s ease-in-out infinite alternate;
                    }

                    @keyframes capsuleFloat {
                        0% { transform: translateY(0); }
                        100% { transform: translateY(3.5px); }
                    }

                    /* Sweeping Edge Glint Flare */
                    .corner-light-flare {
                        position: absolute;
                        top: -1px;
                        left: -50px;
                        width: 100px;
                        height: 2px;
                        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.95), var(--notification-color), transparent);
                        animation: sweepFlare 5s ease-in-out infinite;
                        pointer-events: none;
                    }

                    @keyframes sweepFlare {
                        0% { left: -100px; opacity: 0; }
                        15% { opacity: 1; }
                        85% { opacity: 1; }
                        100% { left: 450px; opacity: 0; }
                    }

                    /* Ring Progress SVG Container */
                    .status-ring-container {
                        width: 36px;
                        height: 36px;
                        position: relative;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        flex-shrink: 0;
                    }

                    .progress-ring-svg {
                        transform: rotate(-90deg);
                        transform-origin: 50% 50%;
                    }

                    .progress-ring-path {
                        transition: stroke-dashoffset 50ms linear;
                    }

                    /* Breathing Central Gradient Orb */
                    .fluid-emblem-orb {
                        position: absolute;
                        width: 26px;
                        height: 26px;
                        border-radius: 50%;
                        background: linear-gradient(135deg, var(--notification-color), var(--notification-color));
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        z-index: 2;
                        box-shadow: 0 3px 8px var(--notification-color-glow);
                        overflow: hidden;
                    }

                    .orb-inner-flare {
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: radial-gradient(circle at 35% 35%, rgba(255, 255, 255, 0.7), transparent 60%);
                        mix-blend-mode: overlay;
                        z-index: 1;
                    }

                    .orb-icon-wrap {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        z-index: 2;
                        animation: orbPulse 2s ease-in-out infinite alternate;
                    }

                    @keyframes orbPulse {
                        0% { transform: scale(0.94); filter: brightness(1); }
                        100% { transform: scale(1.06); filter: brightness(1.1); }
                    }

                    .capsule-content-section {
                        flex: 1;
                        display: flex;
                        flex-direction: column;
                        gap: 1px;
                        min-width: 0;
                    }

                    /* Ultra-Micro High-End HUD Meta Labels */
                    .hud-meta-label {
                        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
                        font-size: 8px;
                        font-weight: 800;
                        color: var(--notification-color);
                        letter-spacing: 0.15em;
                        text-transform: uppercase;
                        opacity: 0.85;
                        margin-bottom: 1px;
                    }

                    .capsule-title {
                        font-size: 14px;
                        font-weight: 800;
                        color: #0f172a;
                        margin: 0;
                        letter-spacing: -0.02em;
                    }

                    .capsule-message {
                        font-size: 12.5px;
                        font-weight: 500;
                        color: #475569;
                        line-height: 1.45;
                        margin: 0;
                        letter-spacing: -0.01em;
                    }

                    /* Minimal Elegant Close Button */
                    .capsule-close-btn {
                        background: rgba(15, 23, 42, 0.05);
                        border: none;
                        border-radius: 50%;
                        width: 22px;
                        height: 22px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
                        color: #64748b;
                        flex-shrink: 0;
                    }

                    .capsule-close-btn:hover {
                        background: rgba(15, 23, 42, 0.09);
                        color: #0f172a;
                        transform: rotate(90deg) scale(1.05);
                    }

                    /* Mobile Layout Adjustment */
                    @media (max-width: 640px) {
                        .notify-overlay {
                            padding-top: 16px;
                        }

                        .hologram-hud-capsule {
                            padding: 12px 16px 12px 14px;
                            gap: 12px;
                            border-radius: 20px;
                        }

                        .capsule-title {
                            font-size: 13px;
                        }

                        .capsule-message {
                            font-size: 12px;
                        }
                    }
                `}</style>
            </div>
        );
    } else {
        return <></>;
    }
}

export default Notify;